// useVersionWatcher
// ----------------------------------------------------------------------------
// Lightweight runtime detection of "the deployed backend has been updated
// since this tab loaded". Pairs with the Seviye-1 ChunkLoadError reload to
// give users a *non-blocking* heads-up before they hit a stale-bundle error.
//
// How it works
//   1. On first poll, remember the version the server reports.
//   2. On every subsequent poll (default: every 5 minutes), compare.
//   3. If different → fire `onUpdateAvailable(newVersion)` exactly ONCE
//      per session so we don't spam the user.
//
// The backend `/api/version` endpoint resolves to either the deploy-time
// APP_VERSION env (preferred — set this to the git SHA in CI) or the process
// start time, which naturally rotates whenever the service is redeployed.
//
// The hook is intentionally storage-free at runtime: the "first version"
// lives only in a useRef so closing/refreshing the tab gives a clean slate.
import { useEffect, useRef } from 'react';

const DEFAULT_POLL_MS = 5 * 60 * 1000; // 5 minutes

export default function useVersionWatcher({
  onUpdateAvailable,
  pollMs = DEFAULT_POLL_MS,
  enabled = true,
} = {}) {
  const initialVersion = useRef(null);
  const notified       = useRef(false);

  useEffect(() => {
    if (!enabled) return undefined;

    const url = `${process.env.REACT_APP_BACKEND_URL || ''}/api/version`;

    let cancelled = false;

    async function check() {
      try {
        // Use fetch instead of axios to keep this hook dependency-free and
        // dodge any auth interceptors that might attach JWTs unnecessarily.
        const res = await fetch(url, { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        const v = String(data?.version || '').trim();
        if (!v || cancelled) return;

        if (initialVersion.current == null) {
          initialVersion.current = v;
          return;
        }
        if (v !== initialVersion.current && !notified.current) {
          notified.current = true;
          try { onUpdateAvailable && onUpdateAvailable(v); } catch { /* ignore */ }
        }
      } catch {
        // Offline / transient — try again on next tick.
      }
    }

    // First check shortly after mount so we record the baseline quickly.
    const firstId = setTimeout(check, 1500);
    const intId   = setInterval(check, pollMs);

    // Re-check on tab refocus to catch deploys that happened while the
    // tab was backgrounded.
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      clearTimeout(firstId);
      clearInterval(intId);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, pollMs, onUpdateAvailable]);
}
